import { Check, ChevronDown, Search, X } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

/**
 * Touch-first searchable select.
 *
 * The dashboard uses this for "client" and "vehicle" pickers in the
 * mission form. We don't reach for a third-party select library
 * because:
 *   - No transitive deps (every kB on a 4G phone hurts).
 *   - Full control over touch targets (each row is 56px) and
 *     keyboard / pointer mix.
 *   - Trivial enough that maintaining it ourselves is cheaper than
 *     fighting an external API on every minor design change.
 *
 * Behaviour:
 *   - The trigger button shows the current selection or the
 *     placeholder. Tapping it opens an absolutely-positioned panel
 *     with a search input at the top and a scrollable result list.
 *   - The search input filters by `searchableFields` (defaults to
 *     just `label`). Matching is case-insensitive substring — no
 *     fuzzy-find magic, drivers know what their clients are called.
 *   - "Aucun" row at the top of the list lets the user clear a
 *     previous selection (returns `null`).
 *   - Clicking outside, pressing Escape, or selecting an option
 *     closes the panel.
 *
 * Stays uncontrolled internally for the open/search state — the
 * parent only owns the actual value, which keeps wiring simple.
 */

export interface SelectSearchOption {
  value: string
  label: string
  /** Secondary text shown on a second line ("camion", email…). */
  detail?: string
}

type SearchableField = keyof Pick<SelectSearchOption, 'label' | 'detail'>

// Module-level constant so the default doesn't allocate a new
// array on every render — that would invalidate the `filtered`
// useMemo's deps each render and re-run the filter for nothing.
const DEFAULT_SEARCHABLE_FIELDS: ReadonlyArray<SearchableField> = ['label', 'detail']

interface SelectSearchProps {
  label: string
  placeholder?: string
  value: string | null
  onChange: (value: string | null) => void
  options: readonly SelectSearchOption[]
  /** Fields to search across — defaults to `label` + `detail`. `value` is never searched. */
  searchableFields?: ReadonlyArray<SearchableField>
  loading?: boolean
  disabled?: boolean
  error?: string | null
  /** Show the "Aucun" clear-row — defaults to true. */
  allowClear?: boolean
}

export function SelectSearch({
  label,
  placeholder = 'Sélectionner…',
  value,
  onChange,
  options,
  searchableFields = DEFAULT_SEARCHABLE_FIELDS,
  loading = false,
  disabled = false,
  error,
  allowClear = true,
}: SelectSearchProps) {
  const triggerId = useId()
  const panelId = useId()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Find the selected option once per change so the trigger label
  // is stable across re-renders.
  const selected = useMemo(
    () => options.find((opt) => opt.value === value) ?? null,
    [options, value],
  )

  // Filter options by query. We always show every option when
  // empty — `trim` normalizes the trailing-space case from
  // autocomplete suggestions.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === '') return options
    return options.filter((opt) =>
      searchableFields.some((field) => {
        const v = opt[field]
        return typeof v === 'string' && v.toLowerCase().includes(q)
      }),
    )
  }, [options, query, searchableFields])

  // Click-outside / Escape close. Effects only attach while the
  // panel is open to keep idle pages cheap.
  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (
        containerRef.current !== null &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Focus the search input when the panel opens — tapping a select
  // on mobile should immediately show the keyboard.
  useEffect(() => {
    if (open) inputRef.current?.focus()
    else setQuery('') // reset filter so next open starts fresh
  }, [open])

  function handleSelect(next: string | null): void {
    onChange(next)
    setOpen(false)
  }

  return (
    <div className="flex flex-col gap-1" ref={containerRef}>
      <label htmlFor={triggerId} className="text-sm font-medium text-text">
        {label}
      </label>

      <button
        id={triggerId}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        disabled={disabled || loading}
        onClick={() => setOpen((o) => !o)}
        className={[
          'min-h-[3rem] w-full rounded-xl border-2 px-4 py-2',
          'text-base bg-surface text-left',
          'flex items-center justify-between gap-2',
          'transition-colors duration-150',
          'focus:outline-none',
          error !== undefined && error !== null && error !== ''
            ? 'border-danger'
            : open
              ? 'border-brand'
              : 'border-surface-border',
          disabled || loading
            ? 'opacity-60 cursor-not-allowed'
            : 'cursor-pointer',
        ].join(' ')}
      >
        <span
          className={[
            'truncate',
            selected !== null ? 'text-text' : 'text-text-muted',
          ].join(' ')}
        >
          {loading
            ? 'Chargement…'
            : selected !== null
              ? selected.label
              : placeholder}
        </span>
        <ChevronDown
          size={20}
          className={[
            'shrink-0 text-text-muted transition-transform duration-150',
            open ? 'rotate-180' : '',
          ].join(' ')}
          aria-hidden="true"
        />
      </button>

      {/* Helper / error slot — keeps layout stable like Input. */}
      <p
        className={[
          'min-h-[1.25rem] text-sm',
          error !== undefined && error !== null && error !== ''
            ? 'text-danger'
            : 'text-text-muted',
        ].join(' ')}
      >
        {error ?? ''}
      </p>

      {open ? (
        <div
          id={panelId}
          role="listbox"
          className={[
            'relative z-20',
            // Float the panel just below the trigger; on small
            // screens it would collide with the keyboard if we
            // anchored absolutely. Keeping it inline + below means
            // the page scrolls naturally when the keyboard opens.
            'mt-1 rounded-xl border-2 border-brand bg-surface shadow-md',
            'overflow-hidden',
          ].join(' ')}
        >
          {/* Search input */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-border">
            <Search size={18} className="text-text-muted shrink-0" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher…"
              className={[
                'flex-1 bg-transparent text-base outline-none',
                'placeholder-text-muted',
              ].join(' ')}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {query !== '' ? (
              <button
                type="button"
                aria-label="Effacer la recherche"
                onClick={() => setQuery('')}
                className="text-text-muted hover:text-text"
              >
                <X size={16} aria-hidden="true" />
              </button>
            ) : null}
          </div>

          {/* Results — capped height + scroll for long lists. */}
          <ul
            className="max-h-72 overflow-y-auto overscroll-contain"
            // overscroll-contain prevents the body from rubber-band-
            // scrolling on iOS when the user reaches the end.
          >
            {allowClear ? (
              <li>
                <button
                  type="button"
                  onClick={() => handleSelect(null)}
                  className={[
                    'w-full text-left px-4 py-3 min-h-[3rem]',
                    'flex items-center gap-2',
                    'text-text-muted italic',
                    'hover:bg-surface-muted active:bg-surface-muted',
                    'border-b border-surface-border',
                  ].join(' ')}
                >
                  Aucun
                </button>
              </li>
            ) : null}
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-text-muted text-center">
                Aucun résultat
              </li>
            ) : (
              filtered.map((opt) => {
                const isActive = opt.value === value
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      onClick={() => handleSelect(opt.value)}
                      className={[
                        'w-full text-left px-4 py-3 min-h-[3.5rem]',
                        'flex items-center justify-between gap-2',
                        'hover:bg-surface-muted active:bg-surface-muted',
                        isActive ? 'bg-info-soft text-brand font-medium' : 'text-text',
                      ].join(' ')}
                    >
                      <span className="flex flex-col min-w-0">
                        <span className="truncate">{opt.label}</span>
                        {opt.detail !== undefined && opt.detail !== '' ? (
                          <span className="text-xs text-text-muted truncate">
                            {opt.detail}
                          </span>
                        ) : null}
                      </span>
                      {isActive ? (
                        <Check size={18} className="text-brand shrink-0" aria-hidden="true" />
                      ) : null}
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
